import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import WebsiteNav from './WebsiteNav';
import WebsiteFooter from './WebsiteFooter';

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.06 } } };

const SCREENSHOTS = [
  { img: '/screenshots/dashboard.jpg', title: 'Dashboard', category: 'Planning', desc: 'Bird\'s-eye view of all projects with KPIs, task completion, and recent activity.' },
  { img: '/screenshots/projects.jpg', title: 'Projects', category: 'Planning', desc: 'Full lifecycle project management with status, contract value, and client details.' },
  { img: '/screenshots/teams.jpg', title: 'Teams', category: 'Planning', desc: 'Organize teams by trade, assign leaders, and link to projects.' },
  { img: '/screenshots/milestones.jpg', title: 'Milestones & Tasks', category: 'Planning', desc: 'Project milestones with tasks, priorities, assignees, and subtask breakdown.' },
  { img: '/screenshots/users.jpg', title: 'User Management', category: 'Admin', desc: 'Manage users, roles, invitations, and access control.' },
  { img: '/screenshots/budgets.jpg', title: 'Budgets', category: 'Finance', desc: 'Set material, labor, and overhead budgets per project and WBS activity.' },
  { img: '/screenshots/invoices.jpg', title: 'Invoices', category: 'Finance', desc: 'Create invoices with line items, VAT calculations, and payment tracking.' },
  { img: '/screenshots/financial-dashboard.jpg', title: 'Financial Dashboard', category: 'Finance', desc: 'Revenue, costs, profit margins, cash flow, and project-level financial health.' },
  { img: '/screenshots/payroll.jpg', title: 'Payroll', category: 'HR', desc: 'Employee management, salary structures, payroll runs, and payslips.' },
  { img: '/screenshots/inventory.jpg', title: 'Inventory', category: 'Operations', desc: 'Warehouses, stock levels, purchase orders, and stock movements.' },
  { img: '/screenshots/accounting.jpg', title: 'Accounting', category: 'Finance', desc: 'Chart of Accounts, General Ledger, Trial Balance, P&L, and Balance Sheet.' },
  { img: '/screenshots/role-permissions.jpg', title: 'Role Permissions', category: 'Admin', desc: '11 roles with editable access control matrix.' },
  { img: '/screenshots/reports.jpg', title: 'Weekly Reports', category: 'Reports', desc: 'Auto-generated Technical, Operational, and Financial reports.' },
];

const CATEGORIES = ['All', ...new Set(SCREENSHOTS.map(s => s.category))];

export default function ModulesPage() {
  const [filter, setFilter] = useState('All');
  const [lightbox, setLightbox] = useState(null);

  const filtered = filter === 'All' ? SCREENSHOTS : SCREENSHOTS.filter(s => s.category === filter);

  return (
    <div className="bg-[#0B0F19] min-h-screen text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <WebsiteNav />

      <section className="pt-32 pb-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.25em] uppercase text-blue-400 mb-4">All Modules</motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Module Gallery
            </motion.h1>
            <motion.p variants={fadeUp} className="text-slate-400 mt-4 max-w-xl mx-auto">
              Browse through every module of ProjectBook Pro&trade; with real screenshots.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Filter Tabs */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-10">
        <div className="flex flex-wrap justify-center gap-2" data-testid="module-filters">
          {CATEGORIES.map(c => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-4 py-1.5 text-xs font-medium rounded-full border transition-all ${
                filter === c
                  ? 'bg-[#3B82F6] border-[#3B82F6] text-white'
                  : 'border-white/10 text-slate-400 hover:text-white hover:border-white/25'
              }`}
              data-testid={`filter-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Screenshot Grid */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((s, i) => (
              <motion.div
                key={s.title}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="group cursor-pointer"
                onClick={() => setLightbox(s)}
                data-testid={`module-card-${i}`}
              >
                <div className="bg-[#1A1F36] border border-white/8 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-white/20 hover:shadow-2xl">
                  <div className="overflow-hidden">
                    <img src={s.img} alt={s.title} className="w-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-sm text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                      <span className="text-[10px] font-medium text-slate-500 border border-white/10 px-2 py-0.5 rounded-full">{s.category}</span>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Lightbox - Full Screen */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#0B0F19]/98 backdrop-blur-xl flex flex-col"
            onClick={() => setLightbox(null)}
            data-testid="lightbox"
          >
            <div className="flex items-center justify-between px-4 sm:px-8 py-4 shrink-0">
              <div>
                <h3 className="text-white font-semibold text-sm sm:text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{lightbox.title}</h3>
                <p className="text-xs text-slate-500">{lightbox.desc}</p>
              </div>
              <button onClick={() => setLightbox(null)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="lightbox-close">
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center px-4 sm:px-8 pb-4 overflow-auto" onClick={e => e.stopPropagation()}>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                className="w-full max-w-7xl"
              >
                <div className="bg-[#1A1F36] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                  <div className="flex items-center gap-1.5 px-4 py-2.5 bg-[#141827] border-b border-white/5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                    <span className="text-xs text-slate-500 ml-3">ProjectBook Pro&trade; &middot; {lightbox.title}</span>
                  </div>
                  <img src={lightbox.img} alt={lightbox.title} className="w-full" />
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="py-20 bg-[#0E1220] text-center">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Want to explore these modules live?
        </h2>
        <Link to="/demo" className="inline-block px-8 py-3.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-lg transition-all text-sm shadow-lg shadow-blue-500/25" data-testid="modules-cta-btn">
          Login for Demo
        </Link>
      </section>

      <WebsiteFooter />
    </div>
  );
}
